// (c) 2023 ABBYY Development, Inc.

import { FC, KeyboardEvent, useRef, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Button,
    DateSelector,
    IconButton,
    Input,
    Popover,
    FilterFill,
    MagnifierSearch,
    Typography
} from 'components';
import { useBoolean } from 'hooks';
import BooleanFilter from './BooleanFilter';
import MultiSelectFilter from './MultiSelectFilter';
import NumberRangeFilter from './NumberRangeFilter';
import IntegerFilter from './IntegerFilter';
import { useFilter } from 'providers';

interface FilterProps {
    resourceId: string;
    columnId: string;
    filterType: string;
    options?: string[];
    defaultMin?: number;
    defaultMax?: number;
}

const FilterButtonRow = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;

    > div {
        margin-left: 3px;
    }
`;

const Filter: FC<FilterProps> = ({
    resourceId,
    columnId,
    filterType,
    options,
    defaultMax,
    defaultMin
}) => {
    const { state: filterState, setFilter } = useFilter();
    const [filter, set] = useState(filterState?.[resourceId]?.[columnId]);
    const { t } = useTranslation();
    const anchorEl = useRef();
    const [isOpen, show, hide] = useBoolean();
    const [isDisabled, disable, enable] = useBoolean();

    const handleKeyDown = (event: KeyboardEvent) => {
        switch (event.key) {
            case 'Enter': {
                !isDisabled && applyFilter();
                break;
            }
            case 'Esc':
                hide();
                break;
        }
    };

    const applyFilter = () => {
        if (
            filter?.value == null ||
            (Array.isArray(filter?.value) && !filter?.value?.length)
        ) {
            clearFilter();
            return;
        }

        setFilter(resourceId, columnId, filter);
        hide();
    };

    const clearFilter = () => {
        setFilter(resourceId, columnId, null);
        set(null);
        hide();
    };

    return (
        <>
            <IconButton
                size={'small'}
                icon={<FilterFill size={16} color={'primary'} />}
                onClick={show}
                isActive={!!filter}
                ref={anchorEl}
            />
            <Popover
                open={isOpen}
                onClose={hide}
                anchorEl={anchorEl.current}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right'
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right'
                }}
            >
                <Box m={4} onKeyDown={handleKeyDown}>
                    {filterType === 'text' && (
                        <Input
                            isFullWidth={true}
                            icon={<MagnifierSearch size={16} />}
                            margin={'none'}
                            value={filter?.value || ''}
                            onChange={(event) =>
                                set({ value: event.target.value })
                            }
                            isFocused={true}
                        />
                    )}
                    {filterType === 'integer' && (
                        <IntegerFilter filter={filter} setFilter={set} />
                    )}
                    {filterType === 'date' && (
                        <DateSelector filter={filter} setFilter={set} />
                    )}
                    {filterType === 'boolean' && (
                        <BooleanFilter filter={filter} setFilter={set} />
                    )}
                    {filterType === 'numberRange' && (
                        <NumberRangeFilter
                            filter={filter}
                            setFilter={set}
                            defaultMax={defaultMax}
                            defaultMin={defaultMin}
                            enableFilter={enable}
                            disableFilter={disable}
                        />
                    )}
                    {filterType === 'multiSelect' && (
                        <MultiSelectFilter
                            filter={filter}
                            set={set}
                            options={options}
                        />
                    )}
                </Box>
                <Box m={2}>
                    <FilterButtonRow>
                        <Button type={'text'} onClick={hide}>
                            {t('general.Close')}
                        </Button>
                        <Button onClick={clearFilter} type="text">
                            <Typography color="error">
                                {t('expressions.reset_filter')}
                            </Typography>
                        </Button>
                        <Button onClick={applyFilter} isDisabled={isDisabled}>
                            {t('general.Filter')}
                        </Button>
                    </FilterButtonRow>
                </Box>
            </Popover>
        </>
    );
};

export default Filter;
